/** @jsxImportSource @emotion/react */
import css from '@emotion/css/macro'
import type { FC } from 'react'
import { useState } from 'react'

import colors from '@sportninja/common/constants/appColors'

import { Flex } from 'src/components/Layout'
import DropdownArrowIcon from 'src/components/icons/DropdownArrowIcon'
import { media } from 'src/components/Responsive'
import GameListRowV2 from 'src/components/List/GameList/game-list-row'
import MobileGameListRow from 'src/components/List/GameList/MobileGameListRow'
import type {
  Game,
  GameStatusType,
  GameType,
} from 'src/components/List/GameList/game-list-row/types'
import Text from 'src/components/ts-components/Text'

export interface DayOfGamesProps {
  /** Already-formatted heading — the caller owns the venue-local formatting. */
  date: string
  games?: Game[]
  gameStatusTypes?: GameStatusType[]
  gameTypes?: GameType[]
  isMobile?: boolean
  isSoccer?: boolean
  isHockey?: boolean
  onClickFirstGame?: () => void
  scheduleId?: string | null
  isMySchedule?: boolean
}

// `Flex` and the two row components are untyped JS. Casting the components once
// here is honest about that, and keeps the cast from spreading to every prop.
const FlexRow = Flex as unknown as FC<Record<string, unknown>>
const DesktopRow = GameListRowV2 as unknown as FC<Record<string, unknown>>
const MobileRow = MobileGameListRow as unknown as FC<Record<string, unknown>>

/**
 * One collapsible day section of the List view.
 *
 * Lives here rather than inside `GameList/index.jsx` so the list view model can
 * grow without dragging a shared presentational component along with it. It is
 * still re-exported from `GameList` — MySchedule and OrgSchedule import it from
 * there, and pointing them at this file instead would make two unrelated pages
 * depend on the Schedule feature folder for a plain presentational component.
 */
const DayOfGames = ({
  date,
  games = [],
  gameStatusTypes,
  gameTypes,
  isMobile = false,
  isSoccer = false,
  isHockey = false,
  onClickFirstGame = () => {},
  scheduleId = null,
  isMySchedule = false,
}: DayOfGamesProps) => {
  const [isOpen, setIsOpen] = useState(true)

  return (
    <div
      css={css`
        ${isOpen ? 'margin-bottom: 16px;' : ''}

        ${(media as Record<string, string>).mobile} {
          padding: 0 8px;
        }
      `}
    >
      <FlexRow
        onClick={() => setIsOpen(!isOpen)}
        alignItems='center'
        justifyContent='space-between'
        className='day-of-games'
        css={css`
          border-radius: 8px;
          background-color: ${colors.SECONDARY_400};
          padding: 8px 16px;
          margin-bottom: 16px;
          cursor: pointer;
          transition: filter 0.2s ease-in-out;
          &:hover {
            filter: brightness(1.2);
          }
          svg {
            transition: transform 0.1s ease-in-out;
            ${!isOpen && 'transform: rotate(180deg);'}
          }
        `}
      >
        <Text variant='captionSmall' color={colors.NEUTRAL_0} weight='semiBold'>
          {date}
        </Text>
        <DropdownArrowIcon color={colors.NEUTRAL_0} />
      </FlexRow>
      {isOpen &&
        games.map((game, idx) => {
          const hasNextSibling = games.length > 1 && idx !== games.length - 1
          const shared = {
            game,
            hasNextSibling,
            gameStatusTypes,
            gameTypes,
            isSoccer,
            isHockey,
            scheduleId,
            isMySchedule,
          }

          if (isMobile) {
            return <MobileRow key={game.id} {...shared} />
          }

          return (
            <DesktopRow
              key={game.id}
              {...shared}
              isFirstGame={idx === 0}
              onClickFirstGame={onClickFirstGame}
            />
          )
        })}
    </div>
  )
}

export default DayOfGames
